import { useMemo } from 'react';
import { displayDateTime } from '@zonofi/common/functions/datetime-operations';
import { getInteractiveTemplateWithButtons } from '@zonofi/common/functions/message-template-functions/get-interactive-template';
import { getUnknownTemplateV2 } from '@zonofi/common/functions/message-template-functions/get-unknown-template';
import { getNoIntentTemplate } from '@zonofi/common/functions/message-template-functions/get-no-intent-template';
import { getMediaTemplateFromObject } from '@zonofi/common/functions/message-template-functions/get-media-template';
import {
  getValidTemplate,
  getButtonsFromTemplate,
} from '@zonofi/common/functions/message-template-functions/get-valid-template';
import {
  Media,
  MessagePayload,
  MsgTemplate,
  NoIntentTemplate,
  TemplateButtons,
} from '@zonofi/common/schema/message/inferface';
import { interactiveSchema } from '@zonofi/common/schema/message/interactive-template-schema';
import { textSchema } from '@zonofi/common/schema/message/message-schema';
import { noIntentTemplateSchema } from '@zonofi/common/schema/message/template-schema';
import {
  MsgStats,
  RedingtonMediaRequest,
  ChannelDetails,
  getUserId,
  store,
  getWorkspaceId,
} from '@zonofi/zono-money-store';
import { isEmpty } from 'lodash';
import { MessageType } from '@zonofi/common/schema/message/enum';
import { GenerateLinkParams } from '@zonofi/common';

export interface ConversationMessageProps {
  message: MessagePayload;
  timetoken?: string;
  stats?: MsgStats;
  isInternalNote?: boolean;
  isWidgetChat?: boolean;
  chatId?: string;
  channel?: ChannelDetails;
}

const extractSubtype = (url: any): string => {
  if (typeof url === 'object') url = url?.[0]?.url;
  if (typeof url !== 'string' || !url) return 'official';

  try {
    const cleanedUrl = url.replace(/^[a-zA-Z]+:\/\//, '').split(/[?#]/)[0];
    const pathParts = cleanedUrl.split('/').filter(Boolean);

    const subtype = pathParts.find((part) =>
      ['official', 'provisional'].includes(part.toLowerCase())
    );
    return subtype || 'official';
  } catch {
    return 'official';
  }
};

export const generateMediaParams = (
  type: MessageType,
  srcdata: any,
  mimeType: 'image' | 'document',
  media: Media,
  messageId: string
): GenerateLinkParams => {
  const data =
    Array.isArray(srcdata) && srcdata.length > 0 ? srcdata[0] : srcdata;
  switch (type) {
    case MessageType.INVOICE:
      return {
        documentType: 'invoice',
        entityId: data?.invoiceId || data?.id,
        mimeType: mimeType === 'image' ? 'image/*' : 'application/pdf',
        subType: data?.docs ? extractSubtype(data?.docs) : 'official',
      };
    case MessageType.DISPATCH: {
      return {
        documentType: 'dispatch',
        entityId: data?.dispatchId || data?.id,
        mimeType: mimeType === 'image' ? 'image/*' : 'application/pdf',
        // subType: 'courier_receipt',
      };
    }
    default:
      return {
        cntType: media?.cntType,
        name: media?.name ?? media?._id,
        messageId: messageId,
      };
  }
};

const ZonoBotUserIds = {
  ledgerBot: 'e49f0374-7f51-40ce-ae67-4f64f9e3d234',
  setupBot: '8d828c29-9219-47cf-be5b-ba937dd7dc8c',
  orderBot: 'fccfcd59-9f4d-4992-99f6-8871383acc0c',
  productBot: '2fb643a8-d547-4633-bed3-e6a76fd7b99b',
  claimBot: '8d828c29-9219-47cf-be5b-ba937dd7dc8c',
  invoiceBot: 'f3a21459-1a08-41b4-bfda-f304e95c7b14',
};

export const isOwnMessage = (uuid: string, channel?: ChannelDetails) => {
  const userId = getUserId(store.getState());
  const workspaceId = getWorkspaceId(store.getState());
  const memberIds: string[] = [];
  if (channel && channel.type === 'C') {
    (channel.teamMembers || []).forEach((x) => memberIds.push(x?.userId || ''));
  }
  const botUserIds = Object.values(ZonoBotUserIds);
  const allIds = [userId, workspaceId, ...memberIds, ...botUserIds];
  return allIds.includes(uuid);
};

export const useGetConversationMessage = ({
  message,
  timetoken,
  channel,
}: ConversationMessageProps) => {
  const {
    actionBy,
    user,
    source,
    type: msgType,
    text: msgText,
    userDetails,
    data,
    intent,
    template: originalTemplate = {},
    sType,
    media: msgMedia,
    fileUrl,
    _id: msgId = '',
    waConfig,
    cAt,
    sId,
  } = message;

  const media = Array.isArray(msgMedia) ? msgMedia : [];

  const {
    id: userId = '',
    fName,
    firstName,
    mobile,
  } = actionBy || user || userDetails || data?.user || data?.userDetails || {};
  const isOwn = isOwnMessage(sId || userId, channel);

  const userName = fName || firstName || mobile || '-';
  const isWhatsapp = source === 'whatsapp';
  const time = timetoken || cAt;
  const msgTime = displayDateTime(String(time));

  let type: MessageType;
  if (
    msgType === MessageType.TYPEBOT ||
    msgType === MessageType.WHATSAPP_TEMPLATE
  ) {
    type = (intent as MessageType) ?? MessageType.TEXT;
  } else if (msgType === MessageType.ISSUE) {
    type = (sType as MessageType) ?? MessageType.TEXT;
  } else {
    type = (msgType as MessageType) ?? MessageType.TEXT;
  }

  const generateFileUrlPayload = (
    mimeType: 'image' | 'document'
  ): GenerateLinkParams => {
    if (type === MessageType.MEDIA && media?.[0]?.provider === 'redington') {
      return {
        id: media?.[0]?._id,
        waConfigId: media?.[0]?.waConfigId || waConfig?.id,
      } as RedingtonMediaRequest;
    }
    const payload = generateMediaParams(type, data, mimeType, media[0], msgId);
    return payload;
  };

  const text = textSchema.safeParse(msgText).success ? msgText : undefined;

  const template = useMemo(() => {
    let result: {
      template: MsgTemplate;
      buttons: TemplateButtons[];
    } = {
      template: getUnknownTemplateV2(text ?? ''),
      buttons: [],
    };

    if (!isEmpty(originalTemplate)) {
      if (type === MessageType.INTERACTIVE) {
        if (interactiveSchema.safeParse(originalTemplate).success) {
          result = getInteractiveTemplateWithButtons(originalTemplate);
        }
      } else if (noIntentTemplateSchema.safeParse(originalTemplate).success) {
        const currentTemplate = Object.entries(
          originalTemplate as NoIntentTemplate
        )?.[0];
        const { caption, filename, url } = currentTemplate?.[1] || {};
        const type = currentTemplate?.[0] === 'document' ? 'document' : 'image';
        const temp = getNoIntentTemplate(url, type, caption, filename);
        result = { template: temp, buttons: [] };
      } else {
        result = {
          template: getValidTemplate(originalTemplate),
          buttons: getButtonsFromTemplate(originalTemplate),
        };
      }
    } else if (media.length > 0) {
      result = {
        template: getMediaTemplateFromObject(media, text ?? '', fileUrl || ''),
        buttons: [],
      };
    }

    return result;
  }, [originalTemplate, type, text, media, fileUrl]);

  // console.log(timetoken, originalTemplate, template.template);

  return {
    userId,
    userName,
    isWhatsapp,
    msgTime,
    ...template,
    msgId: msgId,
    generateFileUrlPayload,
    msgType: type,
    isOwn,
  };
};
